import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const StoreContext = createContext(null);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  const [currentStore, setCurrentStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user and stores on mount
  useEffect(() => {
    const initStore = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const userData = JSON.parse(userStr);
          setUser(userData);

          // Load stores
          const api = axios.create({
            baseURL: `${BACKEND_URL}/api`,
            headers: { Authorization: `Bearer ${token}` }
          });

          const storesRes = await api.get('/stores');
          setStores(storesRes.data);

          // Determine current store based on role
          if (userData.role === 'super_admin' || userData.role === 'admin') {
            // Super admin can see all stores, default to first or saved preference
            const savedStoreId = localStorage.getItem('selectedStoreId');
            if (savedStoreId) {
              const store = storesRes.data.find(s => s.id === savedStoreId);
              setCurrentStore(store || null);
            }
          } else if (userData.store_id) {
            // Store admin or seller - use their assigned store
            const store = storesRes.data.find(s => s.id === userData.store_id);
            setCurrentStore(store || null);
          }
        } catch (error) {
          console.error('Error initializing store context:', error);
        }
      }
      setLoading(false);
    };

    initStore();
  }, []);

  // Switch store (only for super_admin)
  const switchStore = (storeId) => {
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      const store = stores.find(s => s.id === storeId);

      setCurrentStore(store || null);
      if (storeId) {
        localStorage.setItem('selectedStoreId', storeId);
      } else {
        localStorage.removeItem('selectedStoreId');
      }
    }
  };

  // Helper to convert Hex to HSL for Shadcn compatibility
  const updateThemeColor = (color) => {
    if (!color) return;

    // Simple check if it's hex
    if (color.startsWith('#')) {
      let r = parseInt(color.substring(1, 3), 16);
      let g = parseInt(color.substring(3, 5), 16);
      let b = parseInt(color.substring(5, 7), 16);

      r /= 255; g /= 255; b /= 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }

      // Convert to degrees and percentages
      h = Math.round(h * 360);
      s = Math.round(s * 100);
      l = Math.round(l * 100);

      const hslValue = `${h} ${s}% ${l}%`;
      document.documentElement.style.setProperty('--primary', hslValue);
      document.documentElement.style.setProperty('--ring', hslValue);
    }
  };

  useEffect(() => {
    if (currentStore?.color_primary) {
      updateThemeColor(currentStore.color_primary);
    } else {
      // Reset to default mirishop red
      updateThemeColor('#E52B50');
    }
  }, [currentStore]);

  // Check if user can switch stores
  const canSwitchStores = user?.role === 'super_admin' || user?.role === 'admin';

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Check if user is store admin
  const isStoreAdmin = user?.role === 'store_admin';

  // Check if user is seller
  const isSeller = user?.role === 'seller';

  // Get store filter for API calls
  const getStoreFilter = () => {
    if (currentStore?.id) {
      return `store_id=${currentStore.id}`;
    }
    return '';
  };

  // Update user info
  const updateUser = (newUser) => {
    setUser(newUser);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
      // Update store if needed
      if (newUser.store_id && !canSwitchStores) {
        const store = stores.find(s => s.id === newUser.store_id);
        setCurrentStore(store || null);
      }
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    setCurrentStore(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedStoreId');
  };

  const value = {
    currentStore,
    stores,
    user,
    loading,
    switchStore,
    canSwitchStores,
    isSuperAdmin,
    isStoreAdmin,
    isSeller,
    getStoreFilter,
    updateUser,
    logout,
    setStores,
    setCurrentStore,
    refreshStores: async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const api = axios.create({
            baseURL: `${BACKEND_URL}/api`,
            headers: { Authorization: `Bearer ${token}` }
          });
          const storesRes = await api.get('/stores');
          setStores(storesRes.data);

          if (currentStore) {
            const updated = storesRes.data.find(s => s.id === currentStore.id);
            if (updated) setCurrentStore(updated);
          }
        } catch (error) {
          console.error('Error refreshing stores:', error);
        }
      }
    }
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContext;
